import { useMemo } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@altimateai/lego";
import { ArrowDownIcon } from "@/assets/icons";
import { useUsers } from "@/helpers/hooks";
import { isUserDeleted } from "@/helpers/utils";
import { useAppState } from "@/modules/app/useAppContext";
import { ExplorerTracking } from "@/lib/tracking/explorer";
import { useOpportunities } from "../../useOpportunity";
import { OwnerAvatar } from "./OwnerAvatar";

interface OwnerPickerProps {
  opportunity_id?: string;
  assignedOwner?: string | null;
  setAssignedOwner: (owner: string) => void;
  startDate?: string;
  endDate?: string;
}

const UNASSIGNED = "-1";

/**
 * Owner picker matched to the "Discover (A)" design — an avatar + name
 * trigger with an avatar menu. Reuses the exact user list, mutation
 * (`useOpportunities`) and tracking that `OwnerAssignerDropdown` uses, so
 * behaviour is unchanged.
 */
const OwnerPicker = ({
  opportunity_id,
  assignedOwner,
  setAssignedOwner,
  startDate,
  endDate,
}: OwnerPickerProps) => {
  const { mutation } = useOpportunities();
  const { mutate } = mutation;
  const { loggedinUser: user } = useAppState();
  const { users } = useUsers();

  const userList = useMemo(() => {
    if (!users) return [{ label: "Unassigned", value: UNASSIGNED }];
    return [
      { label: "Unassigned", value: UNASSIGNED },
      ...users
        .filter(u => u.first_name || u.last_name)
        .filter(u => !isUserDeleted(u) || u.id.toString() === assignedOwner)
        .map(u => {
          const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ");
          return {
            label: isUserDeleted(u) ? `${fullName} (deleted)` : fullName,
            value: u.id.toString(),
          };
        }),
    ];
  }, [users, assignedOwner]);

  const current = assignedOwner || UNASSIGNED;
  const selected = userList.find(o => o.value === current);
  const triggerLabel =
    current === UNASSIGNED ? "Assign" : (selected?.label ?? "").split(" ")[0] || "Owner";

  const handleChange = (owner: string) => {
    setAssignedOwner(owner);
    ExplorerTracking.opportunityOwnerAssigned(opportunity_id ?? "", owner);
    mutate({
      changes: [{ opportunity_id: opportunity_id ?? "", owner, status: undefined }],
      user_id: user?.id,
      start_date: startDate ?? "",
      end_date: endDate ?? "",
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`Owner: ${current === UNASSIGNED ? "Unassigned" : (selected?.label ?? "")}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: "2px 4px",
            borderRadius: 6,
          }}
        >
          <OwnerAvatar label={selected?.label ?? ""} value={current} size={26} />
          <span
            style={{
              fontSize: 13,
              color: current === UNASSIGNED ? "#98A2B3" : "#475467",
              whiteSpace: "nowrap",
            }}
          >
            {triggerLabel}
          </span>
          <ArrowDownIcon width={13} height={13} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="al-min-w-[184px] al-max-h-72 al-overflow-auto">
        {userList.map(opt => (
          <DropdownMenuItem key={opt.value} onSelect={() => handleChange(opt.value)}>
            <span style={{ marginRight: 9, display: "inline-flex" }}>
              <OwnerAvatar label={opt.label} value={opt.value} size={24} />
            </span>
            {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default OwnerPicker;
